import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import * as Icons from 'lucide-react';
import { ArrowRight, Clock, Video, Phone, MapPin, Star, ShieldCheck, CalendarCheck } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { api } from '../lib/api';
import { useLang } from '../context/LanguageContext';

const LOCATION_ICON = { online: Video, phone: Phone, in_person: MapPin, custom: CalendarCheck };

export default function BookLandingPage() {
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const nav = useNavigate();

  useEffect(() => {
    api.listTypes().then((d) => setTypes(d)).finally(() => setLoading(false));
  }, []);

  return (
    <>
      <Header />
      <section className="bg-gradient-to-b from-[#fbf7ee] to-white pt-16 pb-12 border-b border-neutral-100">
        <div className="max-w-[1200px] mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto">
            <p className="section-eyebrow mb-3">Book Appointment</p>
            <h1 className="text-[40px] md:text-[54px] leading-[1.05] font-extrabold text-[#14110f]">
              Schedule with the <span className="text-[#f5b300]">WYSE Tribe</span> team
            </h1>
            <p className="mt-5 text-[16px] text-neutral-600 leading-relaxed">
              Pick a service, choose a time that works for you, and we’ll do the rest. Confirmation arrives instantly – free reschedule up to 12 hours before.
            </p>
            <div className="mt-6 flex items-center justify-center gap-6 text-[13px] text-neutral-600">
              <span className="flex items-center gap-1.5"><ShieldCheck className="w-4 h-4 text-[#c89200]" /> Secure booking</span>
              <span className="flex items-center gap-1.5"><Star className="w-4 h-4 text-[#c89200] fill-current" /> 4.9 rating</span>
              <span className="flex items-center gap-1.5"><CalendarCheck className="w-4 h-4 text-[#c89200]" /> Instant confirmation</span>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-[1200px] mx-auto px-6">
          <div className="flex items-end justify-between mb-8">
            <div>
              <p className="section-eyebrow mb-2">Step 1 of 3</p>
              <h2 className="text-[28px] md:text-[34px] font-extrabold text-[#14110f]">Choose an appointment type</h2>
            </div>
            <Link to="/contact" className="hidden md:inline text-[13.5px] text-neutral-500 hover:text-[#c89200] transition">Need help choosing? →</Link>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="animate-pulse rounded-xl bg-neutral-100 h-56" />
              ))}
            </div>
          ) : types.length === 0 ? (
            <div className="text-center py-20 text-neutral-500">No appointment types available yet.</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {types.map((t, i) => {
                const LocIcon = LOCATION_ICON[t.location_type] || Video;
                const featured = i === 0;
                return (
                  <button
                    key={t.id}
                    onClick={() => nav(`/book/${t.id}`)}
                    className={`group text-left rounded-xl p-7 transition-all duration-300 hover:-translate-y-1.5 overflow-hidden relative ${
                      featured
                        ? 'bg-[#4a2e1a] text-white shadow-[0_25px_55px_-25px_rgba(74,46,26,0.6)]'
                        : 'bg-[#faf6ec] hover:bg-[#4a2e1a] hover:text-white shadow-[0_15px_40px_-25px_rgba(20,17,15,0.18)]'
                    }`}
                  >
                    <div className="absolute -top-12 -right-12 w-44 h-44 rounded-full bg-[#f5b300]/8" />
                    <div className={`w-12 h-12 rounded-lg flex items-center justify-center mb-5 ${featured ? 'bg-[#f5b300]' : 'bg-[#f5b300]/20 group-hover:bg-[#f5b300]'}`}>
                      <LocIcon className={`w-6 h-6 ${featured ? 'text-[#14110f]' : 'text-[#c89200] group-hover:text-[#14110f]'}`} strokeWidth={1.8} />
                    </div>
                    <h3 className={`text-[19px] font-bold mb-2 ${featured ? 'text-white' : 'group-hover:text-white'}`}>{t.name}</h3>
                    <p className={`text-[13.5px] leading-relaxed mb-5 line-clamp-2 ${featured ? 'text-white/70' : 'text-neutral-500 group-hover:text-white/70'}`}>{t.description}</p>
                    <div className="flex items-center justify-between">
                      <div className={`flex items-center gap-3 text-[12.5px] font-medium ${featured ? 'text-white/85' : 'text-neutral-600 group-hover:text-white/85'}`}>
                        <span className="flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> {t.duration_minutes} min</span>
                        <span className="flex items-center gap-1.5 capitalize"><LocIcon className="w-3.5 h-3.5" /> {t.location_type.replace('_', ' ')}</span>
                      </div>
                      <span className={`font-bold text-[14px] ${featured ? 'text-[#f5b300]' : 'text-[#c89200]'}`}>
                        {t.price > 0 ? `$${t.price}` : 'Free'}
                      </span>
                    </div>
                    <span className={`mt-5 inline-flex items-center gap-1.5 text-[12.5px] font-semibold uppercase tracking-wide ${featured ? 'text-[#f5b300]' : 'text-[#c89200] group-hover:text-[#f5b300]'}`}>
                      Select <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </section>
      <Footer />
    </>
  );
}
